/**
 * @file PreviewTab.js
 * @description Preview tab for viewing a portal page and its child portals
 * within the portal page editor. Supports desktop and mobile preview modes.
 */
import React, { useEffect, useCallback } from 'react';
import { readPortalPage } from '../../services/portalPageService.js';
import styles from '../child-portal-component/PortalPageView.module.scss';
import Spinner from '../ui/spinners/Spinner.js';
import FreeformGrid from '../fluid-grid/FreeformGrid.js';
import PortalPageView from '../child-portal-component/PortalPageView.js';
import usePortalPageLoader from '../../hooks/usePortalPageLoader.js';
import useChildPortalNavigation, {
    clearNavigationStorage,
} from '../../hooks/useChildPortalNavigation.js';

function PreviewTab({ savedPage, previewMode }) {
    const loadFn = useCallback(async id => readPortalPage(id, true), []);

    const {
        fetchedContainers,
        fetchedMobileConfiguration,
        isPageLoading,
        loadPortalPage,
    } = usePortalPageLoader(loadFn);

    const handleClose = useCallback(() => {
        loadPortalPage(savedPage.id);
    }, [savedPage.id, loadPortalPage]);

    const {
        childPortalId,
        navigationStack,
        isShowingChildPortal,
        openChildPortal,
        closeChildPortal,
    } = useChildPortalNavigation({
        parentPageId: savedPage.id,
        onClose: handleClose,
    });

    useEffect(() => {
        clearNavigationStorage();
    }, []);

    useEffect(() => {
        loadPortalPage(savedPage.id);
        closeChildPortal();
    }, [savedPage]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                overflowX: 'auto',
                backgroundColor:
                    previewMode === 'mobile'
                        ? '#ffffff'
                        : 'var(--iNGAGED-Login-Background)',
            }}
        >
            <div
                style={
                    previewMode === 'mobile'
                        ? {
                              border: '1px solid rgb(151, 151, 151)',
                              height: '100%',
                              overflowY: 'auto',
                              width: '375px',
                              margin: '0 auto',
                              backgroundColor:
                                  'var(--iNGAGED-Login-Background)',
                          }
                        : {
                              width: '100%',
                              height: '100%',
                              margin: '0 auto',
                          }
                }
            >
                {isPageLoading ? (
                    <div className="tabLoadingIndicator">
                        <Spinner className={styles.loadingSpinner} />
                    </div>
                ) : isShowingChildPortal ? (
                    <PortalPageView
                        portalIdProp={childPortalId}
                        onClose={closeChildPortal}
                        navigationStack={navigationStack}
                        isPreview={true}
                        previewMode={previewMode}
                        onShowPortalView={openChildPortal}
                    />
                ) : (
                    <FreeformGrid
                        fetchedContainers={fetchedContainers}
                        fetchedMobileConfiguration={fetchedMobileConfiguration}
                        navigationStack={[savedPage.id]}
                        onShowPortalView={openChildPortal}
                        isPreview={true}
                        previewMode={previewMode}
                    />
                )}
            </div>
        </div>
    );
}

export default PreviewTab;
